import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { PasswordRequirements } from '@/features/auth/PasswordRequirements';
import { useLogin, useSignup, useVerifyTwoFA } from '@/features/auth/api';
import { Role } from '@/features/auth/types';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import {
    CheckCircleIcon,
    EyeIcon,
    EyeOffIcon,
    LockIcon,
    MailIcon,
    UserCircleIcon,
    XCircleIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { validatePassword } from '@/lib/passwordValidation';

import { useCurrentUser } from '@/hooks/useApiKey';

import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const authSearchSchema = z.object({
    // .catch keeps a malformed value (e.g. a repeated ?redirect= that parses to an
    // array) from throwing in validateSearch; it degrades to undefined and falls back to /.
    redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/auth')({
    component: AuthPage,
    validateSearch: authSearchSchema,
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AuthPage() {
    const location = useLocation();
    const isSessionExpired = useMemo(() => {
        return !!location.state?.isSessionExpired;
    }, [location]);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string>('');
    const [loginEmail, setLoginEmail] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [show2FA, setShow2FA] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [twoFactorToken, setTwoFactorToken] = useState<string>('');
    const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        isValid: false,
        errors: [] as string[],
        requirements: {
            hasMinLength: false,
            hasUpperCase: false,
            hasNumber: false,
        },
    });

    const [_, setAuthData] = useCurrentUser();
    const registration = useSignup();
    const login = useLogin();
    const verifyTwoFA = useVerifyTwoFA();

    const isLoginEmailValid = EMAIL_REGEX.test(loginEmail);
    const isSignupEmailValid = EMAIL_REGEX.test(signupEmail);
    const showLoginEmailValidation = loginEmail.length > 0;
    const showSignupEmailValidation = signupEmail.length > 0;

    useEffect(() => {
        if (isSessionExpired) {
            toast.error('Session expired. Please log in again.');
        }
    }, [isSessionExpired]);

    useEffect(() => {
        setAgreedToPrivacy(false);
    }, [activeTab]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const currentEmail = activeTab === 'login' ? loginEmail : signupEmail;
        if (!EMAIL_REGEX.test(currentEmail)) {
            setError('Please enter a valid email address');
            return;
        }

        if (activeTab === 'signup' && password !== confirmPassword) {
            setPasswordsMatch(false);
            return;
        }

        setIsLoading(true);

        try {
            if (activeTab === 'signup') {
                const formData = new FormData(e.target as HTMLFormElement);
                const username = formData.get('signup-username')?.toString();
                const password = formData.get('signup-password')?.toString();
                const name = formData.get('signup-name')?.toString();

                if (username && password && name) {
                    await registration.mutateAsync({
                        username,
                        password,
                        name,
                        role: Role.pending,
                    });

                    setPassword('');
                    setConfirmPassword('');
                    setSignupEmail('');
                    setActiveTab('login');
                }
            }

            if (activeTab === 'login') {
                const formData = new FormData(e.target as HTMLFormElement);
                const username = formData.get('login-username')?.toString();
                const password = formData.get('login-password')?.toString();

                if (username && password) {
                    const response = await login.mutateAsync({
                        username,
                        password,
                        rememberMe: false,
                    });

                    if ('requires2Fa' in response && response.requires2Fa) {
                        setTwoFactorToken(response.token || '');
                        setShow2FA(true);
                    } else if ('accessToken' in response) {
                        setAuthData(response);
                        window.location.reload();
                    } else {
                        console.error('Invalid login response');
                        throw new Error('Invalid login response');
                    }
                }
            }
        } catch (error: any) {
            let errorMessage: string;
            switch (error?.response.status) {
                case 401:
                    errorMessage =
                        'Invalid email or password. Please try again.';
                    break;
                case 403:
                    errorMessage =
                        'Your account is locked. Please contact an administrator.';
                    break;
                default:
                    errorMessage =
                        error?.response?.data?.message ||
                        'An error occurred. Please try again.';
            }
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handle2FASubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!twoFactorToken) return;

        setError('');
        setIsLoading(true);

        try {
            const authResponse = await verifyTwoFA.mutateAsync({
                token: twoFactorToken,
                otpCode: otpCode,
            });

            setAuthData(authResponse);
            window.location.reload();
        } catch (error: any) {
            const errorMessage =
                error?.response.status === 403
                    ? 'Your account is locked. Please contact an administrator.'
                    : error?.response?.data?.detail ||
                      error?.message ||
                      'Invalid OTP code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack2FA = () => {
        setShow2FA(false);
        setTwoFactorToken('');
        setOtpCode('');
        setError('');
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (activeTab === 'signup') {
            setPasswordValidation(validatePassword(newPassword));
            if (confirmPassword) {
                setPasswordsMatch(newPassword === confirmPassword);
            }
        }
    };

    const handleConfirmPasswordChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setConfirmPassword(e.target.value);
        setPasswordsMatch(password === e.target.value);
    };

    const EmailValidationIcon = ({ isValid }: { isValid: boolean }) => (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isValid ? (
                <CheckCircleIcon
                    size={18}
                    className="text-green-500"
                    data-testid="EmailValidation__checkIcon"
                />
            ) : (
                <XCircleIcon
                    size={18}
                    className="text-destructive"
                    data-testid="EmailValidation__crossIcon"
                />
            )}
        </div>
    );

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4"
            data-testid="Auth"
        >
            <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">
                <div className="hidden md:flex flex-col items-center justify-center p-8">
                    <Logo className="scale-150" />
                    <p className="text-muted-foreground mt-8 text-center max-w-sm leading-relaxed">
                        Sign in to access your account or create a new one to
                        get started.
                    </p>
                </div>

                <div className="w-full max-w-md mx-auto">
                    <div className="text-center mb-8 md:hidden">
                        <Logo className="justify-center mb-4" />
                    </div>

                    {!show2FA && (
                        <div className="flex w-full mb-6 rounded-lg border bg-muted/50">
                            <button
                                onClick={() => setActiveTab('login')}
                                className={`flex-1 py-3 px-4 text-center font-medium rounded-l-lg transition-all duration-200 ${
                                    activeTab === 'login'
                                        ? 'bg-primary text-white shadow-xs'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                Login
                            </button>
                            <button
                                onClick={() => setActiveTab('signup')}
                                className={`flex-1 py-3 px-4 text-center font-medium rounded-r-lg transition-all duration-200 ${
                                    activeTab === 'signup'
                                        ? 'bg-primary text-white shadow-xs'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                Sign Up
                            </button>
                        </div>
                    )}

                    {activeTab === 'login' && !show2FA && (
                        <Card>
                            <CardHeader className="space-y-2">
                                <CardTitle className="text-center text-2xl">
                                    Welcome back
                                </CardTitle>
                                <CardDescription className="text-center">
                                    Enter your email and password to access your
                                    account
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {error && (
                                    <div
                                        className="mb-4 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
                                        data-testid="AuthLogin__errorMessage"
                                    >
                                        {error}
                                    </div>
                                )}
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-6"
                                >
                                    <div className="space-y-2">
                                        <Label htmlFor="login-username">
                                            Email
                                        </Label>
                                        <div className="relative">
                                            <MailIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="login-username"
                                                data-testid="AuthLogin__emailInput"
                                                name="login-username"
                                                type="email"
                                                placeholder="john@example.com"
                                                value={loginEmail}
                                                onChange={(e) =>
                                                    setLoginEmail(
                                                        e.target.value,
                                                    )
                                                }
                                                className={`pl-10 ${
                                                    showLoginEmailValidation
                                                        ? 'pr-10'
                                                        : ''
                                                } ${
                                                    showLoginEmailValidation &&
                                                    !isLoginEmailValid
                                                        ? 'border-destructive focus-visible:ring-destructive'
                                                        : ''
                                                }`}
                                                required
                                            />
                                            {showLoginEmailValidation && (
                                                <EmailValidationIcon
                                                    isValid={isLoginEmailValid}
                                                />
                                            )}
                                        </div>
                                        {showLoginEmailValidation &&
                                            !isLoginEmailValid && (
                                                <p
                                                    className="text-sm text-destructive"
                                                    data-testid="AuthLogin__emailErrorMessage"
                                                >
                                                    Please enter a valid email
                                                    address
                                                </p>
                                            )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="login-password">
                                            Password
                                        </Label>
                                        <div className="relative">
                                            <LockIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="login-password"
                                                data-testid="AuthLogin__passwordInput"
                                                name="login-password"
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Enter your password"
                                                className="pl-10 pr-10"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword,
                                                    )
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOffIcon size={18} />
                                                ) : (
                                                    <EyeIcon size={18} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <Button
                                        type="submit"
                                        data-testid="AuthLogin__submitButton"
                                        className="w-full h-11"
                                        disabled={
                                            isLoading || !isLoginEmailValid
                                        }
                                    >
                                        {isLoading
                                            ? 'Signing in...'
                                            : 'Sign in'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 'signup' && !show2FA && (
                        <Card>
                            <CardHeader className="space-y-2">
                                <CardTitle className="text-center text-2xl">
                                    Create account
                                </CardTitle>
                                <CardDescription className="text-center">
                                    Enter your details to create a new account
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-6"
                                >
                                    <div className="space-y-2">
                                        <Label htmlFor="signup-name">
                                            Full Name
                                        </Label>
                                        <div className="relative">
                                            <UserCircleIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="signup-name"
                                                name="signup-name"
                                                placeholder="John Doe"
                                                className="pl-10"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="signup-username">
                                            Email
                                        </Label>
                                        <div className="relative">
                                            <MailIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="signup-username"
                                                name="signup-username"
                                                type="email"
                                                placeholder="john@example.com"
                                                value={signupEmail}
                                                onChange={(e) =>
                                                    setSignupEmail(
                                                        e.target.value,
                                                    )
                                                }
                                                className={`pl-10 ${
                                                    showSignupEmailValidation
                                                        ? 'pr-10'
                                                        : ''
                                                } ${
                                                    showSignupEmailValidation &&
                                                    !isSignupEmailValid
                                                        ? 'border-destructive focus-visible:ring-destructive'
                                                        : ''
                                                }`}
                                                required
                                            />
                                            {showSignupEmailValidation && (
                                                <EmailValidationIcon
                                                    isValid={isSignupEmailValid}
                                                />
                                            )}
                                        </div>
                                        {showSignupEmailValidation &&
                                            !isSignupEmailValid && (
                                                <p className="text-sm text-destructive">
                                                    Please enter a valid email
                                                    address
                                                </p>
                                            )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="signup-password">
                                            Password
                                        </Label>
                                        <div className="relative">
                                            <LockIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="signup-password"
                                                name="signup-password"
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Create a password"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                className="pl-10 pr-10"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword,
                                                    )
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOffIcon size={18} />
                                                ) : (
                                                    <EyeIcon size={18} />
                                                )}
                                            </button>
                                        </div>
                                        <PasswordRequirements
                                            validation={passwordValidation}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="signup-confirm-password">
                                            Confirm Password
                                        </Label>
                                        <div className="relative">
                                            <LockIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="signup-confirm-password"
                                                name="signup-confirm-password"
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Confirm your password"
                                                value={confirmPassword}
                                                onChange={
                                                    handleConfirmPasswordChange
                                                }
                                                className={`pl-10 pr-10 ${
                                                    !passwordsMatch &&
                                                    confirmPassword
                                                        ? 'border-destructive focus-visible:ring-destructive'
                                                        : ''
                                                }`}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword,
                                                    )
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOffIcon size={18} />
                                                ) : (
                                                    <EyeIcon size={18} />
                                                )}
                                            </button>
                                        </div>
                                        {!passwordsMatch && confirmPassword && (
                                            <p className="text-sm text-destructive">
                                                Passwords do not match
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-start space-x-2">
                                        <Checkbox
                                            id="privacy-policy"
                                            checked={agreedToPrivacy}
                                            onCheckedChange={(checked) =>
                                                setAgreedToPrivacy(
                                                    checked === true,
                                                )
                                            }
                                            className="mt-1"
                                        />
                                        <Label
                                            htmlFor="privacy-policy"
                                            className="text-sm text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                        >
                                            By creating an account, I
                                            acknowledge and accept Soji's{' '}
                                            <a
                                                href="https://soji.ai/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary underline hover:text-primary/80"
                                            >
                                                Privacy Policy
                                            </a>
                                        </Label>
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full h-11"
                                        disabled={
                                            isLoading ||
                                            !isSignupEmailValid ||
                                            !passwordValidation.isValid ||
                                            (activeTab === 'signup' &&
                                                !passwordsMatch) ||
                                            (activeTab === 'signup' &&
                                                !agreedToPrivacy)
                                        }
                                    >
                                        {isLoading
                                            ? 'Creating account...'
                                            : 'Create account'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {show2FA && (
                        <Card testId="Auth2FA">
                            <CardHeader className="space-y-2">
                                <CardTitle className="text-center text-2xl">
                                    Two-Factor Authentication
                                </CardTitle>
                                <CardDescription className="text-center">
                                    Enter the 6-digit code from your
                                    authenticator app
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {error && (
                                    <div
                                        className="mb-4 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
                                        data-testid="AuthLogin__errorMessage"
                                    >
                                        {error}
                                    </div>
                                )}
                                <form
                                    onSubmit={handle2FASubmit}
                                    className="space-y-6"
                                >
                                    <div className="space-y-2">
                                        <Label htmlFor="otp-code">
                                            Authentication Code
                                        </Label>
                                        <div className="relative">
                                            <LockIcon
                                                size={18}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            />
                                            <Input
                                                id="otp-code"
                                                data-testid="Auth2FA__otpInput"
                                                type="text"
                                                placeholder="123456"
                                                value={otpCode}
                                                onChange={(e) =>
                                                    setOtpCode(e.target.value)
                                                }
                                                className="pl-10 text-center text-lg tracking-widest"
                                                maxLength={6}
                                                pattern="[0-9]{6}"
                                                autoComplete="one-time-code"
                                                required
                                            />
                                        </div>
                                        <p className="text-sm text-muted-foreground text-center">
                                            Enter the 6-digit code from your
                                            authenticator app
                                        </p>
                                    </div>
                                    <div className="flex space-x-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleBack2FA}
                                            className="flex-1"
                                            disabled={isLoading}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            type="submit"
                                            data-testid="Auth2FA__submitButton"
                                            className="flex-1"
                                            disabled={
                                                isLoading ||
                                                otpCode.length !== 6
                                            }
                                        >
                                            {isLoading
                                                ? 'Verifying...'
                                                : 'Sign in'}
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
